import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  CloudArrowUpIcon,
  FunnelIcon,
  LockClosedIcon,
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid,
} from '@heroicons/react/24/solid';
import { userService, domainService } from '../../services/api';
import AddUserModal from './AddUserModal';
import EditUserSidePanel from './EditUserSidePanel';
import ResetPasswordModal from './ResetPasswordModal';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { useAdminData } from '../../context/AdminDataContext';

// Design System Components
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Table, { StatusBadge, ActionMenu, TablePagination } from '../ui/Table';
import Card, { CardHeader } from '../ui/Card';
import UploadProgress from '../ui/UploadProgress';
import { FormModal } from '../ui/Modal';
import logger from '../../utils/logger';

const Users = ({ showNotification }) => {
    const adminData = useAdminData();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const searchTimeoutRef = useRef(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [availableDomains, setAvailableDomains] = useState([]);
    const [isEditSidePanelOpen, setIsEditSidePanelOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        role: '',
        status: '',
        domain: ''
    });
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    
    // Upload progress state
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResults, setUploadResults] = useState(null);
    
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const usersPerPage = 30;

    const handleEditClick = () => {
        if (selectedUsers.length === 1) {
          const userToEdit = users.find(u => u.id === selectedUsers[0] || u._id === selectedUsers[0]);
          
          if (userToEdit) {
            const userWithConsistentId = {
              ...userToEdit,
              id: userToEdit.id || userToEdit._id
            };
            setSelectedUser(userWithConsistentId);
            setIsEditSidePanelOpen(true);
          }
        }
    };

    const handleRowClick = (user) => {
        const userWithConsistentId = {
          ...user,
          id: user.id || user._id
        };
        
        setSelectedUser(userWithConsistentId);
        setIsEditSidePanelOpen(true);
    };

    const fetchUsersAbortRef = useRef(null);

    const fetchUsers = useCallback(async (page = 1, append = false, forceRefresh = false) => {
        // Cancel any in-flight fetch so a stale response can't overwrite newer data
        if (fetchUsersAbortRef.current) {
          fetchUsersAbortRef.current.abort();
        }
        const controller = new AbortController();
        fetchUsersAbortRef.current = controller;

        try {
          if (page === 1) {
            setLoading(true);
          } else {
            setLoadingMore(true);
          }

          let response;

          // Use cache for initial load without search/filters, unless forceRefresh is true
          if (page === 1 && !debouncedSearchQuery && !filters.role && !filters.status && !filters.domain && !forceRefresh) {
            try {
              response = await adminData.getUsers();
              if (!response?.users) {
                throw new Error('No cached data');
              }
              // Format cached response to match expected structure
              response = { data: { users: response.users, pagination: response.pagination } };
            } catch (error) {
              const result = await userService.getUsers({
                search: debouncedSearchQuery,
                ...filters,
                page: page,
                limit: usersPerPage,
              }, { signal: controller.signal });
              response = { data: result };
            }
          } else {
            // Direct API call for searches/filters/pagination or when forceRefresh is true
            const result = await userService.getUsers({
              search: debouncedSearchQuery,
              ...filters,
              page: page,
              limit: usersPerPage
            }, { signal: controller.signal });
            response = { data: result };
          }
          
          // logger.log('Users API response:', response.data);
      
          // Handle successful response with users data
          if (response.data && response.data.users) {
            const formattedUsers = response.data.users.map(user => ({
              ...user,
              id: user.id || user._id,
              _id: user._id || user.id
            }));
            
            if (append && page > 1) {
              setUsers(prevUsers => [...prevUsers, ...formattedUsers]);
            } else {
              setUsers(formattedUsers);
            }
            
            // logger.log(`Loaded ${formattedUsers.length} users${debouncedSearchQuery ? ` for search: "${debouncedSearchQuery}"` : ''}`);
            
            // Check if there are more pages
            const totalUsers = response.data.pagination?.total || formattedUsers.length;
            const totalPages = response.data.pagination?.pages || Math.ceil(totalUsers / usersPerPage);
            const hasMorePages = page < totalPages;
            
            setHasMore(hasMorePages);
            setCurrentPage(page);
          } else {
            // Handle case where response is successful but no users data
            if (!append) {
              setUsers([]);
            }
            setHasMore(false);
          }
          
          setError('');
        } catch (error) {
          // Ignore cancellations from a newer fetch superseding this one
          if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            return;
          }
          logger.error('Error fetching users:', error);

          // Check if it's a 404 error (no data) vs actual server error
          if (error.response?.status === 404) {
            // 404 means endpoint exists but no data found - this is not an error to show to user
            if (!append) {
              setUsers([]);
            }
            setError('');
            setHasMore(false);
          } else if (error.response?.status >= 500) {
             // Server errors should be shown
             setError('Failed to load users - server error');
             showNotification && showNotification('Server error occurred while loading users', 'error');
           } else if (error.response?.status === 403) {
             // Permission errors should be shown
             setError('Access denied');
             showNotification && showNotification('You do not have permission to view users', 'error');
           } else if (!error.response) {
             // Network errors should be shown
             setError('Network error');
             showNotification && showNotification('Network error - please check your connection', 'error');
          } else {
            // Other errors but don't show generic "failed to load" for 404s
            if (!append) {
              setUsers([]);
            }
            setError('');
            setHasMore(false);
          }
        } finally {
          setLoading(false);
          setLoadingMore(false);
        }
    }, [debouncedSearchQuery, filters, showNotification]);

    // Fetch available domains for verification status
    const fetchAvailableDomains = useCallback(async () => {

        try {
            const domainsData = await adminData.getDomains();

            if (domainsData?.domains) {

                setAvailableDomains(domainsData.domains);
            } else {

            }
        } catch (error) {
            logger.error('Error fetching domains:', error);
            // Fallback to direct API call if cache fails
            try {
                const result = await domainService.getDomains();
                if (result?.domains) {
                    setAvailableDomains(result.domains);
                }
            } catch (fallbackError) {
                logger.error('Fallback domain fetch failed:', fallbackError);
            }
        }
    }, []);

    // Debounce search query with enhanced logging
    useEffect(() => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        // logger.log('Debounced search query updated:', searchQuery);
        setDebouncedSearchQuery(searchQuery);
      }, 300);
      
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [searchQuery]);

    useEffect(() => {
      // Reset pagination and fetch when search or filters change
      setCurrentPage(1);
      setHasMore(true);
      fetchUsers(1, false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery, filters]);

    // Fetch domains on component mount for domain verification status
    useEffect(() => {
      fetchAvailableDomains();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial fetch on component mount
    useEffect(() => {
      fetchUsers(1, false);
      return () => {
        if (fetchUsersAbortRef.current) {
          fetchUsersAbortRef.current.abort();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Infinite scroll handler
    const handleScroll = useCallback((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isNearBottom && hasMore && !loadingMore && !loading) {
        fetchUsers(currentPage + 1, true);
      }
    }, [hasMore, loadingMore, loading, currentPage, fetchUsers]);

    // Search handler function with immediate feedback
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        setSelectedUsers([]); // Clear selections when searching
        
        // Reset pagination and loading state for immediate feedback
        if (query !== debouncedSearchQuery) {
            setCurrentPage(1);
            setHasMore(true);
            // Don't clear users immediately to avoid flickering
        }
    }, [debouncedSearchQuery]);

    const formatLastLogin = (date) => {
        if (!date) return 'Never';
        
          const loginDate = new Date(date);
          const now = new Date();
    const diffInMs = now - loginDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
            return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
          } else {
      return loginDate.toLocaleDateString();
        }
    };

    const handleAddUserClick = () => {
        setIsAddUserModalOpen(true);
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        setHasMore(true);
        fetchUsers(1, false, true);
    };

    const handleFilterMenuOpen = () => {
      setShowFilterMenu(!showFilterMenu);
    };

    const toggleSelectAll = (e) => {
      if (e.target.checked) {
        setSelectedUsers(users.map(user => user._id));
      } else {
        setSelectedUsers([]);
      }
    };

    const handleResetPasswordClick = () => {
        if (selectedUsers.length === 1) {
          const userToReset = users.find(u => u.id === selectedUsers[0] || u._id === selectedUsers[0]);

          if (userToReset) {
            const userWithConsistentId = {
              ...userToReset,
              id: userToReset.id || userToReset._id 
            };
            setSelectedUser(userWithConsistentId);
            setIsResetPasswordModalOpen(true);
          }
        }
    };

    const handleResetPassword = useCallback(async ({ userId, password, requireChange }) => {

        try {
          const result = await userService.resetPassword(userId, password, requireChange);

          if (result.success) {
            showNotification && showNotification('Password reset successfully', 'success');
            setIsResetPasswordModalOpen(false);
            setSelectedUser(null);
          }
        } catch (error) {
          logger.error('Error resetting password:', error);
          showNotification && showNotification('Failed to reset password', 'error');
        }
    }, [showNotification]);

    const handleDeleteUser = async (userId) => {
        try {
            const result = await userService.deleteUser(userId);

            if (result.success) {
                // Clear cache and refresh from server
                setCurrentPage(1);
                setHasMore(true);
                fetchUsers(1, false, true);
                
                setSelectedUsers(prevSelected => prevSelected.filter(id => id !== userId));
                showNotification && showNotification('User deleted successfully', 'success');
                setShowDeleteModal(false);
                setUserToDelete(null);
            }
        } catch (error) {
            logger.error('❌ Error deleting user:', error);
            logger.error('📊 Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                userId: userId
            });
            
            const errorMessage = error.response?.data?.message || 'Failed to delete user';
            showNotification && showNotification(errorMessage, 'error');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) return;
        
        try {

            const result = await userService.bulkDelete(selectedUsers);

            if (result.success) {
                // Clear cache and refresh from server
                setCurrentPage(1);
                setHasMore(true);
                fetchUsers(1, false, true);
                
                setSelectedUsers([]);
                showNotification && showNotification(
                    `${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} deleted successfully`, 
                    'success'
                );
                setShowBulkDeleteModal(false);
            }
        } catch (error) {
            logger.error('❌ Error bulk deleting users:', error);
            logger.error('📊 Bulk delete error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                userIds: selectedUsers
            });
            
            const errorMessage = error.response?.data?.message || 'Failed to delete users';
            showNotification && showNotification(errorMessage, 'error');
        }
    };

    const confirmDeleteUser = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmBulkDelete = () => {
        setShowBulkDeleteModal(true);
    };

    const downloadCSVTemplate = async () => {
        try {
            const data = await userService.getBulkUploadTemplate();

            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.setAttribute('hidden', '');
            a.setAttribute('href', url);
            a.setAttribute('download', 'users_import_template.csv');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
            showNotification && showNotification('CSV template downloaded successfully', 'success');
        } catch (error) {
            logger.error('Error downloading template:', error);
            showNotification && showNotification('Failed to download CSV template', 'error');
        }
    };

    const handleCSVUpload = async (file) => {
        let progressInterval;
        try {
            // Reset upload state
            setUploadStatus('uploading');
            setUploadProgress(0);
            setUploadResults(null);

            const formData = new FormData();
            formData.append('csvFile', file);

            // Simulate progress during upload
            progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) return prev;
                    return prev + Math.random() * 20;
                });
            }, 200);
            
            const result = await userService.bulkUpload(formData);

            clearInterval(progressInterval);
            setUploadProgress(100);

            if (result.success) {
                const results = result.results;
                setUploadStatus('success');
                setUploadResults(results);
                
                showNotification && showNotification(
                    `Successfully imported ${results.summary.successfullyCreated} users`, 
                    'success'
                );
                
                // Refresh users list
                fetchUsers(1, false);
                
                // Close modal after a delay to let user see results
                setTimeout(() => {
                    setShowImportModal(false);
                }, 3000);
            }
        } catch (error) {
            clearInterval(progressInterval);
            logger.error('CSV upload error:', error);
            setUploadStatus('error');
            setUploadProgress(0);
            
            // Set error results if available
            if (error.response?.data?.results) {
                setUploadResults({
                    summary: error.response.data.results.summary || { errors: 1, totalProcessed: 0, successfullyCreated: 0, skipped: 0 },
                    details: error.response.data.results.details || {
                        errors: [{ row: 1, email: 'Unknown', error: error.response.data.message || 'Upload failed' }],
                        successful: [],
                        skipped: []
                    }
                });
            } else {
                setUploadResults({
                    summary: { errors: 1, totalProcessed: 0, successfullyCreated: 0, skipped: 0 },
                    details: {
                        errors: [{ row: 1, email: 'Unknown', error: error.response?.data?.message || 'Failed to import users' }],
                        successful: [],
                        skipped: []
                    }
                });
            }
        }
    };

    const handleCloseUploadProgress = () => {
        setUploadStatus('idle');
        setUploadProgress(0);
        setUploadResults(null);
    };

    return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <UserGroupIconSolid className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Users</h1>
            </div>
            <div className="flex items-center space-x-2">
              {selectedUsers.length > 0 && (
                <StatusBadge 
                  status={`${selectedUsers.length}`} 
                  variant="orange" 
                />
              )}
              {selectedUsers.length > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={confirmBulkDelete}
                  icon={<TrashIcon />}
                >
                  Delete ({selectedUsers.length})
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                loading={loading}
                icon={<ArrowPathIcon />}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto" onScroll={handleScroll}>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-3 pb-6">
          {/* Breadcrumbs & Actions Row */}
          <div className="flex items-center justify-between mb-3">
            <BreadcrumbNavigation />
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddUserClick}
                icon={<PlusIcon />}
              >
                Add
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowImportModal(true)}
                icon={<CloudArrowUpIcon />}
              >
                Import
              </Button>
            </div>
          </div>
          
          {/* Compact Search and Filters */}
          <div className="mb-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <SearchInput
                  placeholder="Search users..."
                  onSearch={handleSearch}
                  size="sm"
                  value={searchQuery}
                />
                {searchQuery && searchQuery.trim() && (
                  <>
                    <button
                      onClick={() => handleSearch('')}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 p-1"
                      title="Clear search"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded pointer-events-none">
                      {debouncedSearchQuery === searchQuery ? `${users.length}` : '...'}
                    </div>
                  </>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleFilterMenuOpen}
                icon={<FunnelIcon />}
                className="relative flex-shrink-0"
              >
                {(filters.role || filters.status || filters.domain) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"></div>
                )}
              </Button>
            </div>
        </div>
  
          {/* Users Table */}
          <div className="overflow-x-auto">
            <Table
              columns={[
                {
                  id: 'select',
                  header: (
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      onChange={toggleSelectAll}
                      checked={users.length > 0 && selectedUsers.length === users.length}
                    />
                  ),
                  sortable: false,
                  width: 'w-12',
                  render: (_, user) => (
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      checked={selectedUsers.includes(user._id)}
                      disabled={user.isOwner}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user._id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user._id));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )
                },
                {
                  id: 'user',
                  header: 'User',
                  sortable: true,
                  width: 'min-w-[200px]',
                  render: (_, user) => (
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-orange-600 rounded-md flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-white">
                          {user.firstName?.charAt(0) || '?'}{user.lastName?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {`${user.firstName} ${user.lastName}`}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          @{user.username}
                        </div>
                        {/* Show email on mobile when email column is hidden */}
                        <div className="text-xs text-gray-500 truncate block md:hidden">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'email',
                  header: 'Email',
                  sortable: true,
                  width: 'min-w-[200px] hidden md:table-cell',
                  render: (_, user) => (
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-gray-900 dark:text-white truncate max-w-[180px]">{user.email}</span>
                      {user.email !== 'afzaal@aiinak.com' && (!availableDomains.some(domain => domain.isVerified && user.email.endsWith('@' + domain.domainName))) && (
                        <div className="relative group flex-shrink-0">
                          <div className="w-3 h-3 bg-amber-100 rounded-full flex items-center justify-center">
                            <ExclamationTriangleIcon className="w-2 h-2 text-amber-600" />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  id: 'phone',
                  header: 'Phone',
                  sortable: true,
                  width: 'min-w-[140px] hidden lg:table-cell',
                  render: (_, user) => (
                    <span className="text-sm text-gray-900 dark:text-white">
                      {user.phoneNumber || '-'}
                    </span>
                  )
                },
                {
                  id: 'role',
                  header: 'Role',
                  sortable: true,
                  width: 'min-w-[120px]',
                  render: (_, user) => (
                    <div>
                      <StatusBadge
                        status={user.roleInfo ? user.roleInfo.displayName : user.role}
                        variant={user.roleInfo?.level >= 80 ? 'orange' : user.roleInfo?.level >= 60 ? 'info' : 'default'}
                      />
                      {user.department && (
                        <div className="text-xs text-gray-500 mt-1 truncate hidden lg:block">
                          {user.department}
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  id: 'status',
                  header: 'Status',
                  sortable: true,
                  width: 'min-w-[100px]',
                  render: (_, user) => (
                    <StatusBadge
                      status={user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
                      variant={user.status === 'active' ? 'success' : user.status === 'inactive' ? 'default' : 'error'}
                    />
                  )
                },
                {
                  id: 'lastLogin',
                  header: 'Last Login',
                  sortable: true,
                  width: 'min-w-[120px] hidden lg:table-cell',
                  render: (_, user) => (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatLastLogin(user.lastLoginAt)}
                    </span>
                  )
                },
                {
                  id: 'actions',
                  header: '',
                  sortable: false,
                  width: 'w-16',
                  render: (_, user) => (
                    <ActionMenu
                      actions={[
                        {
                          label: 'Edit User',
                          icon: <PencilIcon />,
                          onClick: () => handleRowClick(user)
                        },
                        ...(!user.isOwner ? [
                          {
                            label: 'Reset Password',
                            icon: <LockClosedIcon />,
                            onClick: () => {
                              setSelectedUsers([user._id]);
                              handleResetPasswordClick();
                            }
                          },
                          {
                            label: 'Delete User',
                            icon: <TrashIcon />,
                            variant: 'danger',
                            onClick: () => confirmDeleteUser(user)
                          }
                        ] : [])
                      ]}
                      row={user}
                    />
                  )
                }
              ]}
            data={users}
            loading={loading}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={(columnId, direction) => {
              setSortColumn(columnId);
              setSortDirection(direction);
              setCurrentPage(1);
              
              // Apply sorting logic
              const sortedUsers = [...users].sort((a, b) => {
                let aValue = '';
                let bValue = '';
                
                switch(columnId) {
                  case 'user':
                    aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
                    bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
                    break;
                  case 'email':
                    aValue = a.email?.toLowerCase() || '';
                    bValue = b.email?.toLowerCase() || '';
                    break;
                  case 'role':
                    aValue = a.role?.toLowerCase() || '';
                    bValue = b.role?.toLowerCase() || '';
                    break;
                  case 'status':
                    aValue = a.status?.toLowerCase() || '';
                    bValue = b.status?.toLowerCase() || '';
                    break;
                  case 'lastLogin':
                    aValue = new Date(a.lastLoginAt || 0).getTime();
                    bValue = new Date(b.lastLoginAt || 0).getTime();
                    break;
                  default:
                    return 0;
                }
                
                if (direction === 'asc') {
                  return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                } else {
                  return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
                }
              });
              
              setUsers(sortedUsers);
            }}
            emptyMessage={(
              <div className="flex flex-col items-center py-8">
                <UserGroupIcon className="w-10 h-10 text-gray-400 mb-3" />
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
                  {searchQuery && searchQuery.trim() ? 'No users match your search' : 'No users found'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {searchQuery && searchQuery.trim() 
                    ? `Try adjusting your search terms or clear the search to see all users.`
                    : 'Users will appear here when available.'}
                </p>
                {searchQuery && searchQuery.trim() ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSearch('')}
                    icon={<XMarkIcon />}
                  >
                    Clear Search
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddUserClick}
                    icon={<PlusIcon />}
                  >
                    Add User
                  </Button>
                )}
              </div>
            )}
            onRowClick={handleRowClick}
            className="shadow-sm min-w-full"
            size="sm"
          />
          </div>

          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="mt-4 flex justify-center py-4">
              <div className="flex items-center space-x-2">
                <ArrowPathIcon className="w-4 h-4 text-orange-500 animate-spin" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Loading more users...</span>
              </div>
            </div>
          )}
          
          {/* End of results indicator */}
          {!hasMore && users.length > 0 && (
            <div className="mt-4 flex justify-center py-2">
              <span className="text-sm text-gray-500">No more users to load</span>
            </div>
          )}
        </div>
      </div>

        {/* Real Add User Modal */}
        <AddUserModal
          open={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          availableDomains={availableDomains}
          onDomainAdded={domain => {
            adminData.clearCache('domains');
            setAvailableDomains(previous => previous.some(d => String(d.id) === String(domain.id)) ? previous : [...previous, domain]);
          }}
          showNotification={showNotification}
          onSubmit={async (newUserData) => {
            try {
              const result = await userService.createUser(newUserData);

              if (result.success) {
                const createdUser = result.user;
                // Add the new user to the grid
                setUsers(prevUsers => [createdUser, ...prevUsers]);
                // Clear cache and refresh from server to ensure grid is up to date
                setCurrentPage(1);
                setHasMore(true);
                fetchUsers(1, false, true);
                // Don't close modal — AddUserModal will show success step with credentials
              }
            } catch (error) {
              logger.error('Error creating user in Users component:', error);
              // Subscription guard (402) returns the reason in `error`; the
              // create route's own validation uses `message`. Read both so the
              // real reason (e.g. "subscription suspended") is shown, not a
              // generic failure.
              const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to create user';
              showNotification && showNotification(errorMessage, 'error');
              throw error; // Re-throw so AddUserModal knows the operation failed
            }
          }}
        />

        {/* Real Edit User Side Panel */}
        <EditUserSidePanel
          isOpen={isEditSidePanelOpen}
          onClose={() => {
            setIsEditSidePanelOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          availableDomains={availableDomains}
          showNotification={showNotification}
          onUserUpdate={async (updatedUser) => {
            try {
              const userId = selectedUser?.id || selectedUser?._id;
              const result = await userService.updateUser(userId, updatedUser);

              if (result.success) {
                const updatedUserData = result.user || updatedUser;
                
                // Ensure consistent ID format
                const normalizedUser = {
                  ...updatedUserData,
                  id: updatedUserData.id || updatedUserData._id || userId,
                  _id: updatedUserData._id || updatedUserData.id || userId
                };
                
                // Update local users list state immediately
                setUsers(prevUsers => 
                  prevUsers.map(user => {
                    const userIdToMatch = user.id || user._id;
                    if (userIdToMatch === userId) {
                      return { ...user, ...normalizedUser };
                    }
                    return user;
                  })
                );
                
                // Update the selected user state so modal shows updated data
                setSelectedUser(normalizedUser);
                
                showNotification && showNotification('User updated successfully', 'success');
                
                // Don't close the panel immediately to show the user the changes were applied
                // setIsEditSidePanelOpen(false);
                // setSelectedUser(null);
              }
            } catch (error) {
              logger.error('Error updating user:', error);
              const errorMessage = error.response?.data?.message || 'Failed to update user';
              showNotification && showNotification(errorMessage, 'error');
            }
          }}
        />

        {/* Real Reset Password Modal */}
        <ResetPasswordModal
          open={isResetPasswordModalOpen}
          onClose={() => {
            setIsResetPasswordModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSubmit={handleResetPassword}
        />

        {/* Delete User Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete User</h3>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Are you sure you want to delete this user?
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {userToDelete.firstName} {userToDelete.lastName} ({userToDelete.email})
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone. All user data and access will be permanently removed.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDeleteUser(userToDelete._id || userToDelete.id)}
                  icon={<TrashIcon />}
                >
                  Delete User
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Multiple Users</h3>
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Are you sure you want to delete {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}?
                    </h4>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  This action cannot be undone. All selected users' data and access will be permanently removed.
                </p>
                <div className="max-h-32 overflow-y-auto bg-gray-50 dark:bg-[#222222] rounded-md p-3">
                  {selectedUsers.map(userId => {
                    const user = users.find(u => u._id === userId || u.id === userId);
                    return user ? (
                      <div key={userId} className="text-sm text-gray-700 dark:text-gray-300 py-1">
                        • {user.firstName} {user.lastName} ({user.email})
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  icon={<TrashIcon />}
                >
                  Delete {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        <FormModal
          isOpen={showImportModal}
          onClose={() => {
            if (uploadStatus !== 'uploading') {
              setShowImportModal(false);
              // Clear upload progress data
              setUploadStatus('idle');
              setUploadProgress(0);
              setUploadResults(null);
            }
          }}
          title="Import Users"
          onSubmit={(e) => {
            e.preventDefault();
            if (uploadStatus === 'uploading') return;
            
            const formData = new FormData(e.target);
            const file = formData.get('csvFile');
            if (file && file.size > 0) {
              handleCSVUpload(file);
            } else {
              showNotification && showNotification('Please select a CSV file to upload', 'error');
            }
          }}
          submitText={uploadStatus === 'uploading' ? 'Uploading...' : 'Import Users'}
          submitDisabled={uploadStatus === 'uploading'}
          size="lg"
        >
          <div className="space-y-6">
            {/* Instructions Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700/50 rounded-xl p-5 shadow-sm">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800/50 rounded-lg flex items-center justify-center">
                  <CloudArrowUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Import Instructions</h4>
              </div>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-2 mb-4">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                  <span>Download the CSV template below to get started</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                  <span>Fill in your user data following the template format exactly</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                  <span>Upload the completed CSV file using the form below</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                  <span>Leave password column empty to auto-generate secure passwords</span>
                </li>
              </ul>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={downloadCSVTemplate}
                className="w-full bg-white dark:bg-[#222222] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] border-blue-200 dark:border-blue-600 text-blue-700 dark:text-blue-300"
                icon={<CloudArrowUpIcon />}
              >
                Download CSV Template
              </Button>
            </div>
            
            {/* File Upload Section */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                Upload CSV File
              </label>
              <div className="relative">
                <input
                  name="csvFile"
                  type="file"
                  accept=".csv"
                  required
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-50 dark:bg-[#2a2a2a] text-gray-900 dark:text-white transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-500 file:text-white hover:file:bg-orange-600 dark:file:bg-orange-600 dark:hover:file:bg-orange-700 file:cursor-pointer file:transition-colors"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Supported format: CSV files only. Maximum file size: 10MB
                </p>
              </div>
            </div>
            
            {/* Upload Progress */}
            {(uploadStatus !== 'idle' || uploadResults) && (
              <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <UploadProgress
                  isUploading={uploadStatus === 'uploading'}
                  progress={uploadProgress}
                  status={uploadStatus}
                  results={uploadResults}
                  onClose={handleCloseUploadProgress}
                  className="border-0 rounded-none"
                />
              </div>
            )}
          </div>
        </FormModal>

        {/* Simple placeholders for other modals */}
        {showFilterMenu && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Users</h3>
                <button
                  onClick={() => setShowFilterMenu(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="member">Member</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Domain Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Domain
                  </label>
                  <select
                    value={filters.domain}
                    onChange={(e) => setFilters(prev => ({ ...prev, domain: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Domains</option>
                    {availableDomains.map(domain => (
                      <option key={domain.domainName} value={domain.domainName}>
                        {domain.domainName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFilters({ role: '', status: '', domain: '' });
                    setCurrentPage(1);
                  }}
                  className="flex-shrink-0"
                >
                  Clear Filters
                </Button>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilterMenu(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setCurrentPage(1);
                      setShowFilterMenu(false);
                      fetchUsers(1);
                    }}
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};

export default Users;
